import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { Building, User, Search, MapPin, Users, Plus, Trash2, X } from 'lucide-react';
import { useFormik, FormikProvider, FieldArray } from 'formik';
import * as Yup from 'yup';
import { COUNTRY_OPTIONS, isSweden as isSwedenCountry } from '../../../../../utils/vatTreatment';
import { searchCompanies, lookupStatusMessage } from '../../../../../services/api/companyLookupApi';
import { formatOrgNumber } from '../../../../../utils/orgNumberUtils';
import { brandSelectTheme } from '../../../Common/reactSelectTheme';

// Same size and colours as every other select in the app (34 px, brand green).
const modalSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    borderRadius: 8,
    borderColor: state.isFocused ? '#44814E' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(68, 129, 78, 0.10)' : 'none',
    fontSize: '0.875rem',
    '&:hover': { borderColor: state.isFocused ? '#44814E' : '#9ca3af' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 6px', color: '#6b7280' }),
  menuPortal: (base) => ({ ...base, zIndex: 1080 }),
  menu: (base) => ({ ...base, borderRadius: 8 }),
  option: (base) => ({ ...base, fontSize: '0.875rem' }),
  groupHeading: (base) => ({ ...base, fontSize: '0.6875rem', fontWeight: 600, color: '#6b7280' }),
};

const countryGroups = [
  { label: 'Sverige', options: COUNTRY_OPTIONS.filter((c) => c.group === 'Sverige') },
  { label: 'EU-länder', options: COUNTRY_OPTIONS.filter((c) => c.group === 'EU') },
  { label: 'Övriga länder', options: COUNTRY_OPTIONS.filter((c) => c.group === 'Övriga') },
];
const selectPortal = typeof document !== 'undefined' ? document.body : null;

const addressTypeOptions = [
  { value: 'billing', label: 'Fakturaadress' },
  { value: 'shipping', label: 'Leveransadress' },
  { value: 'other', label: 'Annan adress' },
];

// Validation Schema with conditions
const validationSchema = Yup.object().shape({
  type: Yup.string().required('Kundtyp måste väljas'),
  name: Yup.string().when('type', {
    is: 'company',
    then: Yup.string().required('Företagsnamn krävs för företag'),
    otherwise: Yup.string().notRequired(),
  }),
  // Required for a SWEDISH company. A GmbH has an HRB number, an oy a Finnish
  // business ID — neither is a 10-digit orgnr, and demanding one blocked
  // foreign customers from being saved at all.
  orgnumber: Yup.string().when(['type', 'country'], {
    is: (type, country) => type === 'company' && isSwedenCountry(country || 'Sverige'),
    then: Yup.string().required('Organisationsnummer krävs för svenska företag'),
    otherwise: Yup.string().notRequired(),
  }),
  first_name: Yup.string().when('type', {
    is: 'individual',
    then: Yup.string().required('Förnamn krävs för privatperson'),
    otherwise: Yup.string().notRequired(),
  }),
  last_name: Yup.string().when('type', {
    is: 'individual',
    then: Yup.string().required('Efternamn krävs för privatperson'),
    otherwise: Yup.string().notRequired(),
  }),
  phone: Yup.string().required('Telefonnummer är obligatoriskt'),
  // D1-6. The label carries the required mark and the field decides the whole
  // VAT treatment (svensk moms / omvänd skattskyldighet / utanför
  // tillämpningsområdet), but nothing validated it — a company could be saved
  // with no Land at all. That is what produced "Testkund Bygg AB" with country
  // NULL, whose bygg flag then had nothing coherent to hang on. Legacy rows
  // with a blank country are untouched: this modal only creates.
  country: Yup.string().nullable().required('Land måste väljas'),
  payment_terms: Yup.number().transform(value => (isNaN(value) ? undefined : value))
    .nullable(true)
    .notRequired(),
  addresses: Yup.array().of(
    Yup.object().shape({
      address_type: Yup.string().oneOf(['billing', 'shipping', 'other']).required(),
      street1: Yup.string().required('Gatuadress krävs'),
      city: Yup.string().required('Ort krävs'),
      postalcode: Yup.string().required('Postnummer krävs'),
      country: Yup.string().nullable(),
    })
  ).min(1, 'Minst en adress måste anges'),
  contacts: Yup.array().when('type', {
    is: 'company',
    then: Yup.array().of(
      Yup.object().shape({
        contact_name: Yup.string().required('Kontaktpersonens namn krävs för företag'),
        contact_email: Yup.string().email('Ogiltig e-post').nullable(),
        contact_phone: Yup.string().nullable(),
        role: Yup.string().nullable(),
      })
    ).min(1, 'Minst en kontaktperson måste anges för ett företag'),
    otherwise: Yup.array().of(
      Yup.object().shape({
        contact_name: Yup.string().nullable(),
        contact_email: Yup.string().email('Ogiltig e-post').nullable(),
        contact_phone: Yup.string().nullable(),
        role: Yup.string().nullable(),
      })
    )
  })
});

const NewCustomerModal = ({ show, handleClose, handleSave, handleUpdate, customerDetails }) => {
  const isEditMode = !!customerDetails;

  // Company register lookup (TIC) state
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const defaultValues = {
    id: '',
    type: 'company',
    orgnumber: '',
    vatnumber: '',
    country: 'Sverige',
    // Construction-business flag — only meaningful when country is Sverige.
    // Form UI disables the checkbox when country !== Sverige and the change
    // handler auto-clears it to keep the (country, is_construction_business)
    // pair coherent. ML 16 kap 13 § applies between two SE construction
    // businesses; for foreign customers it's irrelevant.
    is_construction_business: false,
    name: '',
    first_name: '',
    last_name: '',
    phone: '',
    payment_terms: '',
    addresses: [
      {
        address_type: 'billing',
        street1: '',
        street2: '',
        city: '',
        postalcode: '',
        country: '',
      },
    ],
    contacts: [
      {
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        role: '',
      },
    ],
  };


  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // An unset country stays unset. It used to be coerced to 'Sverige' here,
      // which meant opening a legacy customer and saving any unrelated field
      // silently asserted it was Swedish. New customers still default to
      // Sverige through defaultValues; only an existing blank stays blank.
      const country = values.country || null;
      const isConstruction = isSwedenCountry(country) && Boolean(values.is_construction_business);
      const payload = {
        type: values.type,
        orgnumber: values.type === 'company' ? values.orgnumber || '' : '',
        vatnumber: values.type === 'company' ? values.vatnumber || '' : '',
        country,
        is_construction_business: isConstruction ? 1 : 0,
        name: values.type === 'company' ? values.name || '' : '',
        first_name: values.type === 'individual' ? values.first_name || '' : '',
        last_name: values.type === 'individual' ? values.last_name || '' : '',
        phone: values.phone || '',
        payment_terms: values.payment_terms || '',
        addresses: values.addresses.map((addr) => ({
          address_type: addr.address_type,
          street1: addr.street1 || '',
          street2: addr.street2 || '',
          city: addr.city || '',
          postalcode: addr.postalcode ? addr.postalcode.replace(/\s/g, '') : '', // Normalize postal code
          country: addr.country || '',
        })),
        contacts: values.contacts.map((ct) => ({
          contact_name: ct.contact_name || '',
          contact_email: ct.contact_email || '',
          contact_phone: ct.contact_phone || '',
          role: ct.role || '',
        })),
      };

      if (isEditMode) {
        payload.id = values.id;
        handleUpdate(payload);
      } else {
        handleSave(payload);
      }
      handleClose();
    }
  });

  const { values, errors, touched, setFieldValue } = formik;

  useEffect(() => {
    if (show) {
      if (isEditMode && customerDetails) {
        const {
          id,
          type,
          orgnumber,
          vatnumber,
          country,
          is_construction_business,
          name,
          first_name,
          last_name,
          phone,
          payment_terms,
          addresses = [],
          contacts = [],
        } = customerDetails;

        formik.resetForm({
          values: {
            id: id || '',
            type: type || 'company',
            orgnumber: orgnumber || '',
            vatnumber: vatnumber || '',
            // Not `|| 'Sverige'`: a null country is "we do not know", and
            // showing it as Sweden is what let a German customer look Swedish.
            country: country || '',
            // Defensive: only honor the stored bygg flag when country is SE.
            // Stale data from before the form redesign could have bygg=1 set
            // on a foreign customer; the disable-on-conflict UI prevents
            // re-saving that combo, but we shouldn't display it either.
            is_construction_business:
              isSwedenCountry(country || 'Sverige') &&
              Boolean(Number(is_construction_business || 0)),
            name: name || '',
            first_name: first_name || '',
            last_name: last_name || '',
            phone: phone || '',
            payment_terms: payment_terms || '',
            addresses: addresses.length > 0 ? addresses : [
              {
                address_type: 'billing',
                street1: '',
                street2: '',
                city: '',
                postalcode: '',
                country: '',
              },
            ],
            contacts: contacts.length > 0 ? contacts : [
              {
                contact_name: '',
                contact_email: '',
                contact_phone: '',
                role: '',
              },
            ],
          }
        });
      } else {
        formik.resetForm({ values: defaultValues });
      }

      // Reset the register-lookup state on every open
      setCompanySearchQuery('');
      setCompanySearchResults([]);
      setSearchError(null);
      setIsSearching(false);
    }
  }, [show, isEditMode, customerDetails]);

  // Company search via the backend TIC proxy — explicit action only
  // (the TIC plan is ~200 calls/month platform-wide). Prefill only for
  // customers; no register flags are stored on the customer row.
  const searchCompany = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 3) {
      setSearchError('Ange minst 3 tecken för att söka');
      return;
    }
    setIsSearching(true);
    setSearchError(null);
    setCompanySearchResults([]);
    try {
      const result = await searchCompanies(inputValue.trim());
      if (!result.ok) {
        setSearchError(
          lookupStatusMessage(result.status) ||
          'Kunde inte hämta företagsinformation. Fyll i uppgifterna manuellt.'
        );
        return;
      }
      if (!result.hits || result.hits.length === 0) {
        setSearchError('Inga företag hittades för denna sökning');
        return;
      }
      setCompanySearchResults(result.hits);
    } finally {
      setIsSearching(false);
    }
  };

  const handleCompanySelect = (company) => {
    setCompanySearchResults([]);
    formik.setFieldValue('name', company.name || '');
    formik.setFieldValue('orgnumber', formatOrgNumber(company.registrationNumber) || '');
    formik.setFieldValue('vatnumber', company.vatNumber || '');
    formik.setFieldValue('phone', company.phone || '');
    // COUNTRY_OPTIONS use full Swedish names; TIC returns ISO codes.
    formik.setFieldValue('country', company.country === 'SE' || !company.country ? 'Sverige' : company.country);
    if (company.address) {
      formik.setFieldValue('addresses.0.street1', company.address.street || '');
      formik.setFieldValue('addresses.0.city', company.address.city || '');
      formik.setFieldValue('addresses.0.postalcode', company.address.postalCode || '');
      formik.setFieldValue('addresses.0.country', company.country === 'SE' || !company.country ? 'Sverige' : company.country);
    }
  };

  return (
    <>
      {/* Styles: src/scss/components/_customer-modal.scss */}

      <Modal
        className='fade'
        size='lg'
        show={show}
        onHide={handleClose}
        backdrop="static"
        centered
      >


        <div className="customer-modal-content">
          <FormikProvider value={formik}>
            <form
              onSubmit={(e) => {
                formik.handleSubmit(e);
                // Formik marks fields touched synchronously on submit, so the
                // error nodes exist by the next frame. Without this a failed
                // save on a long form looked like nothing happening: the button
                // did not respond and the reason was off-screen.
                window.requestAnimationFrame(() => {
                  const firstError = e.target.querySelector('.error-message');
                  if (!firstError) return;
                  firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  const field = firstError.previousElementSibling;
                  if (field && typeof field.focus === 'function') field.focus({ preventScroll: true });
                });
              }}
            >
              {/* Header */}
              <div className="customer-modal-header">
                <button type="button" className="close-button" onClick={handleClose}>
                  <X size={16} />
                </button>
                <div className="d-flex align-items-center gap-3">
                  {values.type === 'company' ? <Building size={18} /> : <User size={18} />}
                  <div>
                    <h3 className="mb-0 customer-modal-title">
                      {isEditMode ? 'Redigera kund' : 'Ny kund'}
                    </h3>
                    <p className="mb-0 customer-modal-subtitle">
                      {isEditMode ? 'Uppdatera kundinformation' : 'Skapa en ny kund i systemet'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="customer-modal-body">
                {/* Customer Type Selection */}
                {!isEditMode && (
                  <div className="section-card section-card--type">
                    <span className="form-label" id="customer-type-label">Kundtyp</span>
                    <div className="inv-seg" role="radiogroup" aria-labelledby="customer-type-label">
                      <div className="inv-seg__opt">
                        <input
                          type="radio"
                          id="customer-type-company"
                          name="customer-type"
                          value="company"
                          checked={values.type === 'company'}
                          onChange={() => setFieldValue('type', 'company')}
                        />
                        <label htmlFor="customer-type-company">
                          <Building size={14} />
                          Företag
                        </label>
                      </div>
                      <div className="inv-seg__opt">
                        <input
                          type="radio"
                          id="customer-type-individual"
                          name="customer-type"
                          value="individual"
                          checked={values.type === 'individual'}
                          onChange={() => setFieldValue('type', 'individual')}
                        />
                        <label htmlFor="customer-type-individual">
                          <User size={14} />
                          Privatperson
                        </label>
                      </div>
                    </div>
                    {touched.type && errors.type && (
                      <div className="error-message">⚠ {errors.type}</div>
                    )}
                  </div>
                )}

                {/* Company Search (TIC register lookup) */}
                {!isEditMode && values.type === 'company' && (
                  <div className="search-section" data-testid="customer-search-section">
                    <div className="search-header">
                      <Search size={16} />
                      <div>
                        <h4 className="section-title">Företagssökning</h4>
                        <p className="section-subtitle">Hämta uppgifter från företagsregistret</p>
                      </div>
                    </div>

                    <div className="search-input-group">
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Ange företagsnamn eller organisationsnummer"
                        value={companySearchQuery}
                        onChange={(e) => setCompanySearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            searchCompany(companySearchQuery);
                          }
                        }}
                        disabled={isSearching}
                        data-testid="customer-search-input"
                      />
                      <button
                        type="button"
                        className="search-button"
                        onClick={() => searchCompany(companySearchQuery)}
                        disabled={isSearching || companySearchQuery.trim().length < 3}
                        data-testid="customer-search-button"
                      >
                        <Search size={16} />
                        {isSearching ? 'Söker...' : 'Sök företag'}
                      </button>
                    </div>

                    {searchError && (
                      <div className="error-message" style={{ marginTop: '0.5rem' }} data-testid="customer-search-error">
                        ⚠ {searchError}
                      </div>
                    )}

                    {companySearchResults.length > 0 && (
                      <div style={{ marginTop: '1rem' }} data-testid="customer-search-results">
                        {companySearchResults.map((company, idx) => (
                          <div
                            key={company.ticCompanyId || idx}
                            onClick={() => handleCompanySelect(company)}
                            data-testid={`customer-company-result-${idx}`}
                            style={{
                              background: 'white',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              padding: '0.75rem 1rem',
                              marginBottom: '0.5rem',
                              cursor: 'pointer'
                            }}
                          >
                            <div style={{ fontWeight: '600', color: '#1e293b' }}>{company.name}</div>
                            <div style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                              Org.nr: {formatOrgNumber(company.registrationNumber)}
                              {company.address?.city ? ` · ${company.address.city}` : ''}
                              {company.isCeased ? ' · Upphört' : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Basic Information */}
                <div className="section-card">
                  <div className="section-header">
                    {values.type === 'company' ? <Building size={20} /> : <User size={20} />}
                    <div>
                      <h4 className="section-title">Grundläggande information</h4>
                      <p className="section-subtitle">
                        {values.type === 'company' ? 'Företagsinformation' : 'Personuppgifter'}
                      </p>
                    </div>
                  </div>

                  {values.type === 'company' ? (
                    <div className="form-grid grid-2">
                      <div className="form-group">
                        <label className="form-label required">Företagsnamn</label>
                        <input
                          type="text"
                          className={`form-input ${touched.name && errors.name ? 'error' : ''}`}
                          value={values.name || ''}
                          onChange={(e) => setFieldValue('name', e.target.value)}
                          placeholder="Ange företagsnamn"
                        />
                        {touched.name && errors.name && (
                          <div className="error-message">⚠ {errors.name}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className={`form-label ${isSwedenCountry(values.country || 'Sverige') ? 'required' : ''}`}>
                          {isSwedenCountry(values.country || 'Sverige')
                            ? 'Organisationsnummer'
                            : 'Registreringsnummer (valfritt)'}
                        </label>
                        <input
                          type="text"
                          className={`form-input ${touched.orgnumber && errors.orgnumber ? 'error' : ''}`}
                          value={values.orgnumber || ''}
                          onChange={(e) => setFieldValue('orgnumber', e.target.value)}
                          placeholder={isSwedenCountry(values.country || 'Sverige')
                            ? 'XXXXXX-XXXX'
                            : 't.ex. HRB 86823'}
                        />
                        {touched.orgnumber && errors.orgnumber && (
                          <div className="error-message">⚠ {errors.orgnumber}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">VAT-nummer</label>
                        <input
                          type="text"
                          className="form-input"
                          value={values.vatnumber || ''}
                          onChange={(e) => setFieldValue('vatnumber', e.target.value)}
                          placeholder="SEXXXXXX01"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label required">Telefonnummer</label>
                        <input
                          type="text"
                          className={`form-input ${touched.phone && errors.phone ? 'error' : ''}`}
                          value={values.phone || ''}
                          onChange={(e) => setFieldValue('phone', e.target.value)}
                          placeholder="+46 XX XXX XX XX"
                        />
                        {touched.phone && errors.phone && (
                          <div className="error-message">⚠ {errors.phone}</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="form-grid grid-2">
                      <div className="form-group">
                        <label className="form-label required">Förnamn</label>
                        <input
                          type="text"
                          className={`form-input ${touched.first_name && errors.first_name ? 'error' : ''}`}
                          value={values.first_name || ''}
                          onChange={(e) => setFieldValue('first_name', e.target.value)}
                          placeholder="Ange förnamn"
                        />
                        {touched.first_name && errors.first_name && (
                          <div className="error-message">⚠ {errors.first_name}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label required">Efternamn</label>
                        <input
                          type="text"
                          className={`form-input ${touched.last_name && errors.last_name ? 'error' : ''}`}
                          value={values.last_name || ''}
                          onChange={(e) => setFieldValue('last_name', e.target.value)}
                          placeholder="Ange efternamn"
                        />
                        {touched.last_name && errors.last_name && (
                          <div className="error-message">⚠ {errors.last_name}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label required">Telefonnummer</label>
                        <input
                          type="text"
                          className={`form-input ${touched.phone && errors.phone ? 'error' : ''}`}
                          value={values.phone || ''}
                          onChange={(e) => setFieldValue('phone', e.target.value)}
                          placeholder="+46 XX XXX XX XX"
                        />
                        {touched.phone && errors.phone && (
                          <div className="error-message">⚠ {errors.phone}</div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Betalningsvillkor (dagar)</label>
                    <input
                      type="number"
                      className={`form-input ${touched.payment_terms && errors.payment_terms ? 'error' : ''}`}
                      value={values.payment_terms || ''}
                      onChange={(e) => setFieldValue('payment_terms', e.target.value)}
                      placeholder="30"
                      style={{ maxWidth: '200px' }}
                    />
                    {touched.payment_terms && errors.payment_terms && (
                      <div className="error-message">⚠ {errors.payment_terms}</div>
                    )}
                  </div>
                </div>

                {/* Land (Session C). Defaults to Sverige. The construction-
                    business checkbox below is auto-disabled when this dropdown
                    moves away from Sverige (mutually exclusive — ML 16 kap 13 §
                    only applies between two SE construction businesses). */}
                <div className="section-card">
                  <div className="section-header">
                    <MapPin size={20} />
                    <div>
                      <h4 className="section-title">Land</h4>
                      <p className="section-subtitle">
                        Land där kunden är registrerad. Avgör om fakturan bär svensk moms, omvänd skattskyldighet (EU) eller faller utanför momsens tillämpningsområde.
                      </p>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label required">Land</label>
                    <div style={{ maxWidth: '320px' }}>
                      <Select
                        inputId="customer-country"
                        options={countryGroups}
                        value={COUNTRY_OPTIONS.find((c) => c.value === values.country) || null}
                        placeholder={values.country ? 'Välj land' : 'Ej angivet — välj land'}
                        onChange={(opt) => {
                          const next = opt?.value || '';
                          const previous = values.country || '';
                          setFieldValue('country', next);
                          // Auto-clear bygg flag when leaving Sverige — the
                          // checkbox below will be disabled, so a stale `true`
                          // would otherwise be invisible-but-set in state.
                          if (!isSwedenCountry(next) && values.is_construction_business) {
                            setFieldValue('is_construction_business', false);
                          }
                          // The two Land fields did not talk to each other, so a
                          // customer could be German up here and Swedish on the
                          // address. Addresses that are empty or still carry the
                          // old value follow; one deliberately set to something
                          // else is left alone.
                          (values.addresses || []).forEach((addr, idx) => {
                            const current = addr.country || '';
                            if (!current || current === previous) {
                              setFieldValue(`addresses.${idx}.country`, next);
                            }
                          });
                        }}
                        theme={brandSelectTheme}
                        styles={modalSelectStyles}
                        menuPortalTarget={selectPortal}
                        classNamePrefix="react-select"
                      />
                    </div>
                  </div>
                </div>

                {/* Omvänd skattskyldighet — disabled when country !== Sverige.
                    ML 16 kap 13 § applies between two SE construction
                    businesses; meaningless for foreign customers. */}
                {(() => {
                  const byggDisabled = !isSwedenCountry(values.country || 'Sverige');
                  return (
                    <div className="section-card">
                      <div className="section-header">
                        <Building size={20} />
                        <div>
                          <h4 className="section-title">Omvänd skattskyldighet</h4>
                          <p className="section-subtitle">
                            Markera om kunden är ett byggföretag och omfattas av omvänd skattskyldighet inom byggsektorn (ML 16 kap 13 §).
                          </p>
                        </div>
                      </div>
                      <div className="form-group">
                        <label
                          className="form-label"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: 0,
                            opacity: byggDisabled ? 0.5 : 1,
                            cursor: byggDisabled ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={Boolean(values.is_construction_business) && !byggDisabled}
                            disabled={byggDisabled}
                            onChange={(e) => setFieldValue('is_construction_business', e.target.checked)}
                            style={{ width: 'auto' }}
                          />
                          Byggföretag — omvänd skattskyldighet inom byggsektorn
                        </label>
                        {byggDisabled && (
                          <div style={{ fontSize: '0.8125rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                            Endast tillämpligt för svenska kunder. För utländska kunder hanteras moms via Land ovan (ML 6 kap eller utanför momsens tillämpningsområde).
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Addresses */}
                <div className="section-card">
                  <div className="section-header">
                    <MapPin size={20} />
                    <div>
                      <h4 className="section-title">Adresser</h4>
                      <p className="section-subtitle">Faktura- och leveransadresser</p>
                    </div>
                  </div>

                  <FieldArray name="addresses">
                    {({ push, remove }) => (
                      <>
                        {values.addresses.map((addr, idx) => (
                          <div key={idx} className="address-card">
                            <div className="card-header">
                              <h6 className="card-title">Adress {idx + 1}</h6>
                              {values.addresses.length > 1 && (
                                <button
                                  type="button"
                                  className="remove-button"
                                  onClick={() => remove(idx)}
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>

                            <div className="form-grid grid-4">
                              <div className="form-group">
                                <label className="form-label required">Typ</label>
                                <Select
                                  options={addressTypeOptions}
                                  value={addressTypeOptions.find(o => o.value === addr.address_type)}
                                  onChange={(opt) => setFieldValue(`addresses.${idx}.address_type`, opt.value)}
                                  placeholder="Välj typ"
                                  isSearchable={false}
                                  theme={brandSelectTheme}
                                  styles={modalSelectStyles}
                                  menuPortalTarget={selectPortal}
                                  classNamePrefix="react-select"
                                />
                                {touched.addresses?.[idx]?.address_type && errors.addresses?.[idx]?.address_type && (
                                  <div className="error-message">⚠ {errors.addresses[idx].address_type}</div>
                                )}
                              </div>
                            </div>

                            <div className="form-grid grid-2">
                              <div className="form-group">
                                <label className="form-label required">Gatuadress</label>
                                <input
                                  type="text"
                                  className={`form-input ${touched.addresses?.[idx]?.street1 && errors.addresses?.[idx]?.street1 ? 'error' : ''}`}
                                  value={addr.street1 || ''}
                                  onChange={(e) => setFieldValue(`addresses.${idx}.street1`, e.target.value)}
                                  placeholder="Gatunamn och nummer"
                                />
                                {touched.addresses?.[idx]?.street1 && errors.addresses?.[idx]?.street1 && (
                                  <div className="error-message">⚠ {errors.addresses[idx].street1}</div>
                                )}
                              </div>
                            </div>

                            <div className="form-grid grid-3">
                              <div className="form-group">
                                <label className="form-label required">Ort</label>
                                <input
                                  type="text"
                                  className={`form-input ${touched.addresses?.[idx]?.city && errors.addresses?.[idx]?.city ? 'error' : ''}`}
                                  value={addr.city || ''}
                                  onChange={(e) => setFieldValue(`addresses.${idx}.city`, e.target.value)}
                                  placeholder="Ort"
                                />
                                {touched.addresses?.[idx]?.city && errors.addresses?.[idx]?.city && (
                                  <div className="error-message">⚠ {errors.addresses[idx].city}</div>
                                )}
                              </div>

                              <div className="form-group">
                                <label className="form-label required">Postnummer</label>
                                <input
                                  type="text"
                                  className={`form-input ${touched.addresses?.[idx]?.postalcode && errors.addresses?.[idx]?.postalcode ? 'error' : ''}`}
                                  value={addr.postalcode || ''}
                                  onChange={(e) => setFieldValue(`addresses.${idx}.postalcode`, e.target.value)}
                                  placeholder={isSwedenCountry(addr.country || values.country || 'Sverige')
                                    ? 'XXX XX'
                                    : 'Postnummer'}
                                />
                                {touched.addresses?.[idx]?.postalcode && errors.addresses?.[idx]?.postalcode && (
                                  <div className="error-message">⚠ {errors.addresses[idx].postalcode}</div>
                                )}
                              </div>

                              <div className="form-group">
                                <label className="form-label">
                                  Land
                                  <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.8rem' }}>
                                    {' '}— följer kundens land om du inte ändrar det
                                  </span>
                                </label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={addr.country || ''}
                                  onChange={(e) => setFieldValue(`addresses.${idx}.country`, e.target.value)}
                                  placeholder="Sverige"
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          className="add-button"
                          onClick={() => push({
                            address_type: 'billing',
                            street1: '',
                            street2: '',
                            city: '',
                            postalcode: '',
                            country: '',
                          })}
                        >
                          <Plus size={16} />
                          Lägg till ny adress
                        </button>

                        {touched.addresses && typeof errors.addresses === 'string' && (
                          <div className="error-message">⚠ {errors.addresses}</div>
                        )}
                      </>
                    )}
                  </FieldArray>
                </div>

                {/* Contacts for Companies */}
                {values.type === 'company' && (
                  <div className="section-card">
                    <div className="section-header">
                      <Users size={20} />
                      <div>
                        <h4 className="section-title">Kontaktpersoner</h4>
                        <p className="section-subtitle">Kontaktuppgifter för företaget</p>
                      </div>
                    </div>

                    <FieldArray name="contacts">
                      {({ push, remove }) => (
                        <>
                          {values.contacts.map((contact, index) => (
                            <div key={index} className="contact-card">
                              <div className="card-header">
                                <h6 className="card-title">Kontakt {index + 1}</h6>
                                {values.contacts.length > 1 && (
                                  <button
                                    type="button"
                                    className="remove-button"
                                    onClick={() => remove(index)}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </div>

                              <div className="form-grid grid-3">
                                <div className="form-group">
                                  <label className="form-label required">Namn</label>
                                  <input
                                    type="text"
                                    className={`form-input ${touched.contacts?.[index]?.contact_name && errors.contacts?.[index]?.contact_name ? 'error' : ''}`}
                                    value={contact.contact_name || ''}
                                    onChange={(e) => setFieldValue(`contacts.${index}.contact_name`, e.target.value)}
                                    placeholder="Kontaktpersonens namn"
                                  />
                                  {touched.contacts?.[index]?.contact_name && errors.contacts?.[index]?.contact_name && (
                                    <div className="error-message">⚠ {errors.contacts[index].contact_name}</div>
                                  )}
                                </div>

                                <div className="form-group">
                                  <label className="form-label">E-post</label>
                                  <input
                                    type="email"
                                    className={`form-input ${touched.contacts?.[index]?.contact_email && errors.contacts?.[index]?.contact_email ? 'error' : ''}`}
                                    value={contact.contact_email || ''}
                                    onChange={(e) => setFieldValue(`contacts.${index}.contact_email`, e.target.value)}
                                    placeholder="kontakt@företag.se"
                                  />
                                  {touched.contacts?.[index]?.contact_email && errors.contacts?.[index]?.contact_email && (
                                    <div className="error-message">⚠ {errors.contacts[index].contact_email}</div>
                                  )}
                                </div>

                                <div className="form-group">
                                  <label className="form-label">Telefon</label>
                                  <input
                                    type="text"
                                    className={`form-input ${touched.contacts?.[index]?.contact_phone && errors.contacts?.[index]?.contact_phone ? 'error' : ''}`}
                                    value={contact.contact_phone || ''}
                                    onChange={(e) => setFieldValue(`contacts.${index}.contact_phone`, e.target.value)}
                                    placeholder="+46 XX XXX XX XX"
                                  />
                                  {touched.contacts?.[index]?.contact_phone && errors.contacts?.[index]?.contact_phone && (
                                    <div className="error-message">⚠ {errors.contacts[index].contact_phone}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            className="add-button"
                            onClick={() => push({ contact_name: '', contact_email: '', contact_phone: '', role: '' })}
                          >
                            <Plus size={16} />
                            Lägg till ny kontaktperson
                          </button>

                          {touched.contacts && typeof errors.contacts === 'string' && (
                            <div className="error-message">⚠ {errors.contacts}</div>
                          )}
                        </>
                      )}
                    </FieldArray>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="modal-actions">
                <div className="modal-actions__hint">
                  {isEditMode ? 'Ändringar sparas automatiskt' : 'Alla obligatoriska fält måste fyllas i'}
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="action-button btn-cancel"
                    onClick={handleClose}
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    className="action-button btn-save"
                  >
                    {isEditMode ? (
                      <>
                        <Building size={16} />
                        Uppdatera kund
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Skapa kund
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </FormikProvider>
        </div>
      </Modal>
    </>
  );
};

export default NewCustomerModal;
